import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState
} from "react";

import { logger } from "src/core/globals";
import { Theme } from "src/gen/schema/models/auth";
import { getLocalTheme, setLocalTheme } from "src/utils/theme";

export interface ThemeSchema {
  readonly theme: Theme;
  readonly applyTheme: (
    theme: Theme,
    persistInBackend?: boolean
  ) => Promise<void>;
}

function getInitialTheme(): Theme {
  return getLocalTheme() ?? Theme.LIGHT;
}

export const ThemeContext = createContext<ThemeSchema>({
  theme: Theme.LIGHT,
  applyTheme: (theme: Theme, persistInBackend?: boolean) => {
    // Fallback behavior if a consumer is ever rendered outside ThemeProvider.
    // This keeps Magnetic + localStorage in sync; consumers won't auto re-render
    // without the provider, but the app state stays correct.
    logger.warn(
      "ThemeContext.applyTheme called outside <ThemeProvider>. This will not trigger React re-renders."
    );
    return setLocalTheme(theme, persistInBackend);
  }
});

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());

  const applyTheme = useCallback(
    (next: Theme, persistInBackend?: boolean): Promise<void> => {
      // Avoid redundant state updates / re-renders.
      setTheme((prev) => (prev === next ? prev : next));

      // Persist/apply the requested theme.
      return setLocalTheme(next, persistInBackend);
    },
    [setTheme]
  );

  const value = useMemo(() => ({ theme, applyTheme }), [theme, applyTheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
